import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { useHousehold } from '../contexts/HouseholdContext';

const ICONS = ['🏠','🏡','🏘','🏕','🛖','🏰'];

export default function HouseholdSetupPage() {
  const { t } = useTranslation();
  const { addHousehold } = useHousehold();
  const [tab, setTab]       = useState('create');
  const [name, setName]     = useState('');
  const [icon, setIcon]     = useState('🏠');
  const [code, setCode]     = useState('');
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return setError(t('household.nameRequired'));
    setLoading(true); setError('');
    try {
      const hh = await api.createHousehold({ name: name.trim(), icon });
      addHousehold(hh);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const join = async (e) => {
    e.preventDefault();
    if (!code.trim()) return setError(t('household.codeRequired'));
    setLoading(true); setError('');
    try {
      const hh = await api.joinHousehold(code.trim().toUpperCase());
      addHousehold(hh);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg-base)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '24px 16px', fontFamily: 'DM Sans, sans-serif',
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: 18,
        background: 'var(--brand-muted)',
        border: '1.5px solid oklch(55% 0.19 152 / 25%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 32, marginBottom: 14,
      }}>🏠</div>

      <h2 style={{ fontSize: 24, fontWeight: 700, color: 'var(--fg-1)', marginBottom: 6 }}>
        {t('household.title')}
      </h2>
      <p style={{ fontSize: 14, color: 'var(--fg-3)', marginBottom: 28, textAlign: 'center', maxWidth: 280 }}>
        {t('household.desc')}
      </p>

      {/* Segment */}
      <div className="ft-segment" style={{ width: '100%', maxWidth: 320, marginBottom: 24 }}>
        {[['create', t('household.tabCreate')], ['join', t('household.tabJoin')]].map(([key, label]) => (
          <button key={key} type="button"
            onClick={() => { setTab(key); setError(''); }}
            className={`ft-segment-item ${tab === key ? 'active' : ''}`}
            style={tab === key ? { background: 'var(--brand)' } : {}}
          >{label}</button>
        ))}
      </div>

      {tab === 'create' ? (
        <form onSubmit={create} style={{ width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
            {ICONS.map(i => (
              <button key={i} type="button" onClick={() => setIcon(i)}
                style={{
                  width: 44, height: 44, fontSize: 22, borderRadius: 12, border: 'none',
                  background: icon === i ? 'var(--brand)' : 'var(--bg-elevated)',
                  cursor: 'pointer', transition: 'all 150ms',
                }}>{i}</button>
            ))}
          </div>
          <input value={name} onChange={e => setName(e.target.value)} required
            className="ft-input" placeholder={t('household.namePlaceholder')} autoFocus />
          {error && <p style={{ fontSize: 13, color: 'var(--status-expired)', textAlign: 'center' }}>{error}</p>}
          <button type="submit" disabled={loading} className="ft-btn-primary">
            {loading ? t('household.creating') : t('household.create', { icon, name: name || t('household.defaultName') })}
          </button>
        </form>
      ) : (
        <form onSubmit={join} style={{ width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input value={code} onChange={e => setCode(e.target.value.toUpperCase())} required
            className="ft-input"
            style={{ textAlign: 'center', letterSpacing: '0.2em', fontFamily: 'DM Mono, monospace', fontSize: 18, textTransform: 'uppercase' }}
            placeholder="ABCD1234" maxLength={8} autoFocus />
          <p style={{ fontSize: 13, color: 'var(--fg-4)', textAlign: 'center' }}>
            {t('household.shareHint')}
          </p>
          {error && <p style={{ fontSize: 13, color: 'var(--status-expired)', textAlign: 'center' }}>{error}</p>}
          <button type="submit" disabled={loading} className="ft-btn-primary">
            {loading ? t('household.joining') : t('household.join')}
          </button>
        </form>
      )}
    </div>
  );
}
